
import {
  extractAndOcrProblemsFromPage,
  generateExplanation,
  detectImageRotation,
  classifyPageType,
  extractExplanationsFromPage,
  ocrCroppedImage
} from './geminiService';
import type { ExtractedProblem, Bbox, ProcessedProblem, ExplanationGuidelines, ProgressCallback, BaseProblem, ManualCrop } from '../types';

// pdf.js is loaded from a CDN in index.html, so we declare it here to satisfy TypeScript.
declare const pdfjsLib: any;

interface PageImage {
  image: string; // base64 string
  pageNumber: number;
}

/**
 * Checks if a file is a PDF based on its magic number.
 */
const isPdfFile = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onloadend = (e) => {
      if (e.target?.result) {
        const arr = new Uint8Array(e.target.result as ArrayBuffer).subarray(0, 4);
        const header = arr.reduce((acc, byte) => acc + byte.toString(16), "");
        resolve(header === "25504446"); // "%PDF"
      } else {
        resolve(false);
      }
    };
    fileReader.readAsArrayBuffer(file.slice(0, 4));
  });
};

const fileToArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
  });
};

/**
 * Renders a single page of a PDF to a high-resolution JPEG image.
 */
const renderPdfPageToImage = async (pdfPage: any): Promise<string> => {
  const DPI = 300;
  const scale = DPI / 96; // Standard screen DPI is 96
  const viewport = pdfPage.getViewport({ scale });

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error("Could not get canvas context");

  canvas.height = viewport.height;
  canvas.width = viewport.width;

  await pdfPage.render({ canvasContext: context, viewport: viewport }).promise;

  return new Promise((resolve) => {
      canvas.toBlob(
          (blob) => {
              if (!blob) {
                  throw new Error("Canvas to Blob conversion failed");
              }
              const reader = new FileReader();
              reader.onload = () => {
                  const base64data = (reader.result as string).split(',')[1];
                  resolve(base64data);
              };
              reader.readAsDataURL(blob);
          },
          'image/jpeg',
          0.95 // High quality
      );
  });
};

const trimCanvasWhitespace = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let found = false;

    const THRESHOLD = 240;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const index = (y * width + x) * 4;
            const r = data[index];
            const g = data[index + 1];
            const b = data[index + 2];
            const a = data[index + 3];

            if (a > 0 && (r < THRESHOLD || g < THRESHOLD || b < THRESHOLD)) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
                found = true;
            }
        }
    }

    if (!found) return canvas;

    const PADDING = 5;
    minX = Math.max(0, minX - PADDING);
    minY = Math.max(0, minY - PADDING);
    maxX = Math.min(width, maxX + PADDING);
    maxY = Math.min(height, maxY + PADDING);

    const trimmedWidth = maxX - minX;
    const trimmedHeight = maxY - minY;

    if (trimmedWidth <= 0 || trimmedHeight <= 0) return canvas;

    const trimmedCanvas = document.createElement('canvas');
    trimmedCanvas.width = trimmedWidth;
    trimmedCanvas.height = trimmedHeight;
    const trimmedCtx = trimmedCanvas.getContext('2d');
    if (!trimmedCtx) return canvas;

    trimmedCtx.drawImage(canvas, minX, minY, trimmedWidth, trimmedHeight, 0, 0, trimmedWidth, trimmedHeight);
    return trimmedCanvas;
};

const cropImage = (imageBase64: string, bbox: Bbox): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const PADDING = 30;
      
      let sx = bbox.x_min * img.width - PADDING;
      let sy = bbox.y_min * img.height - PADDING;
      let sWidth = (bbox.x_max - bbox.x_min) * img.width + (PADDING * 2);
      let sHeight = (bbox.y_max - bbox.y_min) * img.height + (PADDING * 2);

      if (sx < 0) { sWidth += sx; sx = 0; }
      if (sy < 0) { sHeight += sy; sy = 0; }
      if (sx + sWidth > img.width) { sWidth = img.width - sx; }
      if (sy + sHeight > img.height) { sHeight = img.height - sy; }

      if (sWidth <= 0 || sHeight <= 0) {
           sx = bbox.x_min * img.width;
           sy = bbox.y_min * img.height;
           sWidth = (bbox.x_max - bbox.x_min) * img.width;
           sHeight = (bbox.y_max - bbox.y_min) * img.height;
      }
      
      const canvas = document.createElement('canvas');
      canvas.width = sWidth;
      canvas.height = sHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return reject(new Error("Could not get canvas context for cropping"));
      }
      
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, sWidth, sHeight);
      ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, sWidth, sHeight);

      const trimmedCanvas = trimCanvasWhitespace(canvas);
      resolve(trimmedCanvas.toDataURL('image/jpeg', 0.95).split(',')[1]);
    };
    img.onerror = reject;
    img.src = `data:image/jpeg;base64,${imageBase64}`;
  });
};

const convertFileToImages = async (file: File): Promise<PageImage[]> => {
    if (await isPdfFile(file)) {
        const fileBuffer = await fileToArrayBuffer(file);
        const pdf = await pdfjsLib.getDocument(fileBuffer).promise;
        const promises = Array.from({ length: pdf.numPages }, async (_, i) => {
            const page = await pdf.getPage(i + 1);
            const image = await renderPdfPageToImage(page);
            return { image, pageNumber: i + 1 };
        });
        return Promise.all(promises);
    } else {
        const image = await fileToBase64(file);
        return [{ image, pageNumber: 1 }];
    }
};


export const processImportJob = async (
  files: File[], 
  importType: 'single' | 'dual',
  folderName: string,
  guidelines: ExplanationGuidelines,
  onProgress: ProgressCallback,
  onSave: (problems: Omit<BaseProblem, 'id' | 'duplicateOf' | 'deletedAt'>[]) => Promise<void>,
  manualCrops?: ManualCrop[]
) => {
    try {
        onProgress("파일 변환 중...", { current: 0, total: 100 });
        
        const sourceFileName = files.length > 0 ? files[0].name : '가져온 문제';
        
        let problemPages: PageImage[] = [];
        let explanationPages: PageImage[] = [];
        
        // ** Branch for Manual Crops **
        if (manualCrops && manualCrops.length > 0) {
             const file = files[0];
             const allPages = await convertFileToImages(file);
             onProgress("사용자 지정 영역 추출 중...", { current: 20, total: 100 });
             
             const problems: ProcessedProblem[] = [];
             const explanations: { problemNumber: number, text: string }[] = [];
             
             for (let i = 0; i < manualCrops.length; i++) {
                 const crop = manualCrops[i];
                 const page = allPages.find(p => p.pageNumber === crop.pageIndex);
                 if (!page) continue;
                 
                 onProgress(`영역 처리 중... (${i+1}/${manualCrops.length})`, { current: 20 + Math.floor((i / manualCrops.length) * 40), total: 100 });
                 
                 const croppedImageBase64 = await cropImage(page.image, crop.bbox);
                 const ocrResult = await ocrCroppedImage(croppedImageBase64, crop.type);
                 
                 let problemNumber = i + 1;
                 if (crop.label) {
                     const num = parseInt(crop.label.replace(/[^0-9]/g, ''));
                     if (!isNaN(num)) problemNumber = num;
                 } else {
                     const match = ocrResult.match(/^(\d+)\./);
                     if (match) problemNumber = parseInt(match[1]);
                 }

                 if (crop.type === 'problem') {
                     let bodyText = ocrResult;
                     let choicesText: string | undefined = undefined;

                     const choiceMatch = bodyText.search(/[①②③④⑤]/);
                     if (choiceMatch !== -1) {
                         choicesText = bodyText.substring(choiceMatch).trim();
                         bodyText = bodyText.substring(0, choiceMatch).trim();
                     }

                     const numberRegex = new RegExp(`^${problemNumber}\\.?\\s*`);
                     bodyText = bodyText.replace(numberRegex, '').trim();

                     problems.push({
                        id: `manual-p-${i}`,
                        problemNumber: problemNumber,
                        croppedImageBase64: croppedImageBase64, 
                        ocrText: bodyText,
                        choices: choicesText,
                        status: 'completed',
                     });
                 } else {
                     explanations.push({
                         problemNumber: problemNumber,
                         text: ocrResult
                     });
                 }
             }
             
             const finalBaseProblems: Omit<BaseProblem, 'id' | 'duplicateOf' | 'deletedAt'>[] = problems.map(p => {
                 const explanation = explanations.find(e => e.problemNumber === p.problemNumber);
                 return {
                    parts: {
                        lines: p.ocrText?.split('\n') || [],
                        type: p.choices ? '객관식' : '주관식',
                        choices: p.choices || null
                    },
                    createdAt: new Date().toISOString(),
                    explanation: explanation ? explanation.text : null,
                    folder: folderName,
                    source: sourceFileName,
                    problemNumber: p.problemNumber,
                    croppedImage: p.croppedImageBase64 || null,
                    subject: null, 
                    majorUnit: null, 
                    subUnit: null,
                    coreIdeas: [],
                    difficultyScore: 5,
                    metadataStatus: 'completed'
                 };
             });
             
             finalBaseProblems.sort((a, b) => (a.problemNumber ?? 0) - (b.problemNumber ?? 0));
             const now = Date.now();
             finalBaseProblems.forEach((p, index) => {
                p.createdAt = new Date(now - index * 1000).toISOString();
             });

             // Chunked Save Logic for Manual Crops
             const SAVE_CHUNK_SIZE = 15;
             for (let i = 0; i < finalBaseProblems.length; i += SAVE_CHUNK_SIZE) {
                 const chunk = finalBaseProblems.slice(i, i + SAVE_CHUNK_SIZE);
                 const count = Math.min(i + SAVE_CHUNK_SIZE, finalBaseProblems.length);
                 
                 onProgress(`저장 중... (${count}/${finalBaseProblems.length})`, { 
                     current: 90 + Math.floor((i / finalBaseProblems.length) * 9), 
                     total: 100 
                 });
                 
                 await onSave(chunk);
                 // Small delay to unblock UI thread
                 await new Promise(resolve => setTimeout(resolve, 100));
             }

             onProgress("완료", { current: 100, total: 100 });
             return;
        }
        
        // ** Standard Auto-Import Logic **
        if (importType === 'dual') {
             const problemFile = files[0];
             const explanationFile = files[1];
             problemPages = await convertFileToImages(problemFile);
             explanationPages = await convertFileToImages(explanationFile);
        } else {
             const file = files[0];
             const allPages = await convertFileToImages(file);
             
             onProgress("페이지 분류 중...", { current: 20, total: 100 });
             for (let i = 0; i < allPages.length; i++) {
                 const type = await classifyPageType({ data: allPages[i].image, mimeType: 'image/jpeg' });
                 if (type === 'problem') problemPages.push(allPages[i]);
                 else if (type === 'explanation') explanationPages.push(allPages[i]);
                 else {
                     problemPages.push(allPages[i]);
                 }
             }
        }
        
        // --- BATCHING Global Queue ---
        
        interface ExtractionTask {
            type: 'problem' | 'explanation';
            page: PageImage;
        }

        const allTasks: ExtractionTask[] = [
            ...problemPages.map(p => ({ type: 'problem' as const, page: p })),
            ...explanationPages.map(p => ({ type: 'explanation' as const, page: p }))
        ];

        const totalTasks = allTasks.length;
        let processedCount = 0;
        
        const problems: ProcessedProblem[] = [];
        const explanations: { problemNumber: number, text: string }[] = [];

        const updateProgress = () => {
            const percent = 30 + Math.floor((processedCount / totalTasks) * 50);
            onProgress(`AI 분석 중... (${processedCount}/${totalTasks})`, { current: percent, total: 100 });
        };

        const MAX_CONCURRENT = 3;
        const BATCH_SIZE = 3; // Process up to 3 pages at once

        const worker = async () => {
            while (allTasks.length > 0) {
                // 1. Pick a batch of tasks of the SAME TYPE
                const batch: ExtractionTask[] = [];
                const firstTask = allTasks[0]; 
                
                if (!firstTask) break;

                for (let i = 0; i < allTasks.length; i++) {
                    if (allTasks[i].type === firstTask.type) {
                        batch.push(allTasks[i]);
                        if (batch.length >= BATCH_SIZE) break;
                    }
                }

                for (const task of batch) {
                    const idx = allTasks.indexOf(task);
                    if (idx > -1) allTasks.splice(idx, 1);
                }

                // 2. Process the batch
                try {
                    const images = batch.map(t => ({ data: t.page.image, mimeType: 'image/jpeg' }));
                    
                    if (firstTask.type === 'problem') {
                        const extractedProblems = await extractAndOcrProblemsFromPage(images);
                        
                        for (const ext of extractedProblems) {
                            // In batch mode, detailed cropping is skipped or imprecise.
                            // We try to crop if batch size is 1 and bbox exists.
                            let croppedImageBase64: string | undefined = undefined;
                            if (batch.length === 1 && ext.figureBbox) {
                                try {
                                    croppedImageBase64 = await cropImage(batch[0].page.image, ext.figureBbox);
                                } catch (e) {
                                    console.warn("Crop failed", e);
                                }
                            }

                            problems.push({
                                id: `p-${ext.problemNumber}-${Math.random()}`,
                                problemNumber: ext.problemNumber,
                                bbox: ext.figureBbox || undefined,
                                croppedImageBase64: croppedImageBase64, 
                                rotationAngle: 0,
                                ocrText: ext.ocrText,
                                choices: ext.choices,
                                status: 'completed',
                                subject: ext.subject,
                                majorUnit: ext.majorUnit,
                                subUnit: ext.subUnit,
                                coreIdeas: ext.coreIdeas
                            });
                        }
                    } else {
                        const extractedExplanations = await extractExplanationsFromPage(images);
                        explanations.push(...extractedExplanations);
                    }
                } catch (e) {
                    console.error(`Error processing batch of ${firstTask.type}`, e);
                } finally {
                    processedCount += batch.length;
                    updateProgress();
                }
            }
        };

        await Promise.all(Array.from({ length: MAX_CONCURRENT }, worker));
        
        onProgress("문제와 해설 매칭 중...", { current: 80, total: 100 });
        
        const finalBaseProblems: Omit<BaseProblem, 'id' | 'duplicateOf' | 'deletedAt'>[] = problems.map(p => {
             const explanation = explanations.find(e => e.problemNumber === p.problemNumber);
             return {
                parts: {
                    lines: p.ocrText?.split('\n') || [],
                    type: p.choices ? '객관식' : '주관식',
                    choices: p.choices || null
                },
                createdAt: new Date().toISOString(),
                explanation: explanation ? explanation.text : null,
                folder: folderName,
                source: sourceFileName,
                problemNumber: p.problemNumber,
                croppedImage: p.croppedImageBase64 || null,
                subject: p.subject || null, 
                majorUnit: p.majorUnit || null, 
                subUnit: p.subUnit || null,
                coreIdeas: p.coreIdeas || [],
                difficultyScore: 5,
                metadataStatus: 'completed'
             };
        });
        
        finalBaseProblems.sort((a, b) => (a.problemNumber ?? 0) - (b.problemNumber ?? 0));

        const now = Date.now();
        finalBaseProblems.forEach((p, index) => {
            p.createdAt = new Date(now - index * 1000).toISOString();
        });
        
        // Chunked Save Logic for Auto-Import
        const SAVE_CHUNK_SIZE = 15;
        for (let i = 0; i < finalBaseProblems.length; i += SAVE_CHUNK_SIZE) {
            const chunk = finalBaseProblems.slice(i, i + SAVE_CHUNK_SIZE);
            const count = Math.min(i + SAVE_CHUNK_SIZE, finalBaseProblems.length);
            
            onProgress(`저장 중... (${count}/${finalBaseProblems.length})`, { 
                current: 90 + Math.floor((i / finalBaseProblems.length) * 9), 
                total: 100 
            });
            
            await onSave(chunk);
            // Small delay to unblock UI thread
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        onProgress("완료", { current: 100, total: 100 });

    } catch (e: any) {
        console.error("Import Job Failed:", e);
        throw e;
    }
};

export const processExamFile = async (
  file: File,
  guidelines: ExplanationGuidelines,
  onProgress: (msg: string, problems: ProcessedProblem[]) => void
) => {
   return;
};
